import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import EmailSubscribeSection from '@/components/EmailSubscribeSection';

export const Footer = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  return (
    <footer className="bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 mt-auto pb-20 md:pb-0" role="contentinfo">
      {/* Email Subscribe Banner - Above footer grid (hidden on homepage which has its own) */}
      {!isHomePage && (
        <div className="border-b border-slate-200 dark:border-slate-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="max-w-xl mx-auto">
              <EmailSubscribeSection variant="compact" className="bg-white dark:bg-slate-800" />
            </div>
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <div className="mb-3 md:mb-4">
              <Logo size="default" />
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-md mb-3 md:mb-4">
              Connecting communities to bring missing loved ones home. Every share, every tip, and every moment of awareness helps bring families closer to answers.
            </p>
            
            {/* Support Button - Full width on mobile */}
            <Link to="/donate" className="block mb-2 md:mb-3">
              <Button 
                className="w-full sm:w-auto bg-pink-50 hover:bg-pink-100 dark:bg-pink-950/30 dark:hover:bg-pink-950/50 text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-800 hover:border-pink-300 dark:hover:border-pink-700 gap-2 rounded-full px-6"
                variant="outline"
              >
                <Heart className="w-4 h-4 fill-pink-500 text-pink-500" aria-hidden="true" />
                Support Our Mission
              </Button>
            </Link>
            
            <Link 
              to="/impact-stories" 
              className="inline-flex items-center gap-1.5 text-sm text-trust-blue-600 hover:text-trust-blue-700 dark:text-trust-blue-400 dark:hover:text-trust-blue-300"
              aria-label="See agency-engagement impact stories"
            >
              See impact stories →
            </Link>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick links">
            <h4 className="font-heading font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">Quick Links</h4>
            <ul className="space-y-1.5 md:space-y-3">
              <li>
                <Link to="/why" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Why LinkTheMissing?
                </Link>
              </li>
              <li>
                <Link to="/browse" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Explore Cases
                </Link>
              </li>
              <li>
                <Link to="/report" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Submit Missing Person Case
                </Link>
              </li>
              <li>
                <Link to="/map" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Interactive Map
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Resources & Help
                </Link>
              </li>
              <li>
                <Link to="/impact-stories" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Impact Stories
                </Link>
              </li>
              <li>
                {/* Direct anchor — see Navbar.jsx for why. */}
                <a href="/blog/index.html" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Blog
                </a>
              </li>
              <li>
                <Link to="/media-kit" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Media Kit
                </Link>
              </li>
              <li>
                <Link to="/amber-alerts" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  AMBER Alerts
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Pricing
                </Link>
              </li>
            </ul>
          </nav>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">Get Help</h4>
            <ul className="space-y-1.5 md:space-y-3">
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-safety-orange-500" />
                <span>Emergency: 911</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-trust-blue-500" />
                <span>NCMEC: 1-800-THE-LOST</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4 text-trust-blue-500" />
                <a href="mailto:replies@linkthemissing.com" className="hover:text-safety-orange-500">
                  replies@linkthemissing.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 text-safety-orange-500 mt-0.5 shrink-0" />
                <span>
                  PO Box 172<br />
                  Mineral Springs, NC 28108<br />
                  United States
                </span>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & About */}
          <div>
            <h4 className="font-heading font-semibold text-foreground mb-3 md:mb-4 text-sm md:text-base">About</h4>
            <ul className="space-y-1.5 md:space-y-3">
              <li>
                <Link to="/why" className="text-trust-blue-600 dark:text-trust-blue-400 hover:text-trust-blue-700 dark:hover:text-trust-blue-300 transition-colors text-sm font-medium">
                  Why LinkTheMissing
                </Link>
              </li>
              <li>
                <Link to="/methodology" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Research Integrity
                </Link>
              </li>
              <li>
                <Link to="/data-sources" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Data Sources
                </Link>
              </li>
              <li>
                <Link to="/databases" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Find a Database
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/disclaimers" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Disclaimers
                </Link>
              </li>
              <li>
                <Link to="/family-verification" className="text-muted-foreground hover:text-safety-orange-500 transition-colors text-sm">
                  Family Verification
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800 mt-8 md:mt-10 pt-6 md:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 md:gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} LinkTheMissing. Every moment counts.
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/terms" className="hover:text-safety-orange-500">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-safety-orange-500">Privacy</Link>
            <span>·</span>
            <Link to="/disclaimers" className="hover:text-safety-orange-500">Disclaimers</Link>
          </div>
        </div>

        {/* Back to top - especially helpful on mobile */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/50 text-center md:hidden mb-2">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-trust-blue-600 dark:hover:text-trust-blue-400 transition-colors py-2"
          >
            <span className="text-sm">↑</span>
            Back to top
          </button>
        </div>
      </div>
    </footer>
  );
};
